import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Gavel, Car, Trophy, Zap, Scale, HelpCircle, ArrowRight, AlertTriangle, Loader2 } from "lucide-react";
import { LEILOEIROS } from "@/lib/simulator";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabaseClient";
import { useEffect } from "react";

const ICONS: Record<string, React.ElementType> = {
  Gavel, Car, Trophy, Zap, Scale, HelpCircle,
};

interface Props {
  onNext: (leiloeiro: string) => void;
  onBack?: () => void;
}

export function StepLeiloeiro({ onNext, onBack }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [registeredIds, setRegisteredIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchRegistered() {
      try {
        const { data, error } = await supabase
          .from("auctioneer_rules")
          .select("leiloeiro_id");
        
        if (error) throw error;
        if (data) {
          const ids = Array.from(new Set(data.map(item => item.leiloeiro_id)));
          setRegisteredIds(ids);
        }
      } catch (err) {
        console.error("Erro ao carregar leiloeiros dinâmicos:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchRegistered();
  }, []);

  const visibleLeiloeiros = LEILOEIROS.filter(l => 
    l.id === "outro" || 
    registeredIds.includes(l.id) || 
    registeredIds.includes(l.name)
  );

  const leiloeiro = LEILOEIROS.find((l) => l.id === selected);

  const handleContinue = () => {
    if (!selected || !leiloeiro) return;
    if (!leiloeiro.oficial && !showAlert) {
      setShowAlert(true);
      return;
    }
    onNext(leiloeiro.name);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="space-y-2">
        <h2 className="text-3xl font-heading font-bold tracking-tight">Qual o leiloeiro?</h2>
        <p className="text-muted-foreground text-lg">Selecione onde o veículo está anunciado para buscarmos os dados.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full py-10 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary/40" />
            <p className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Sincronizando editais...</p>
          </div>
        ) : visibleLeiloeiros.map((l) => {
          const Icon = ICONS[l.icon];
          return (
            <button
              key={l.id}
              onClick={() => { setSelected(l.id); setShowAlert(false); }}
              className={cn(
                "p-6 rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-3 hover:border-primary/50",
                selected === l.id
                  ? "border-primary bg-primary/5 shadow-md scale-[1.02]"
                  : "border-border bg-background/50 hover:bg-muted/30"
              )}
            >
              <div className={cn(
                "w-14 h-14 rounded-full flex items-center justify-center transition-colors",
                selected === l.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              )}>
                <Icon className="w-6 h-6" />
              </div>
              <span className="text-sm font-semibold">{l.name}</span>
            </button>
          );
        })}
      </div>

      {showAlert && !leiloeiro?.oficial && (
        <Alert variant="destructive" className="border-warning bg-warning/10 text-left">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <AlertDescription className="text-warning-foreground ml-2">
            A precisão pode ser afetada pois não teremos acesso automático ao edital. Deseja continuar?
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-4 pt-4">
        {onBack && (
          <Button variant="outline" size="lg" onClick={onBack} className="flex-1">
            Voltar
          </Button>
        )}
        <Button size="lg" onClick={handleContinue} disabled={!selected} className="flex-1 group">
          Continuar
          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </motion.div>
  );
}
